/**
 * PublicHeader - Unified navigation header for all public pages
 * 
 * Based on Landing.tsx header design (CANON v1.4.5 compliant)
 * Features:
 * - Consistent navigation across all public pages
 * - Mobile-first responsive design with Sheet menu
 * - Touch targets ≥44px (BUG-002)
 * - Safe area insets for iPhone notches (BUG-009)
 * - ARIA labels and keyboard navigation (BUG-003, BUG-015)
 * - Focus rings (BUG-007)
 * - Liquid Glass Chrome tier styling
 * - Smooth scroll to anchor sections (BUG-031)
 */

import { sfIcons } from '@/lib/sf-icons'
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"

// Unified icon via sfIcons for consistent stroke-2
const ChevronDown = sfIcons.chevronDown

import { SupabankLogo } from "@/components/brand/SupabankLogo"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"

const darkChromeGlass = semanticTokens.glass.dark.chrome.combined
  .split(" ")
  .map((className) => `dark:${className}`)
  .join(" ")

export interface PublicHeaderProps {
  /** Optional: Override current hash for active state (for pages without hash routing) */
  currentSection?: string
  /** Optional: Show compact version for internal pages */
  variant?: "full" | "compact"
  /** Optional: Custom className */
  className?: string
}

// Navigation links will be translated via i18n
type NavLink = {
  label: string
  href: string
  isDropdown?: boolean
}

const getNavLinks = (t: (key: string) => string): NavLink[] => [
  { label: t("nav.how"), href: "/#how" },
  { label: t("nav.products"), href: "/products", isDropdown: true },
  { label: t("nav.pricing"), href: "/fee" },
  { label: t("nav.coverage"), href: "/coverage" },
  { label: t("nav.support"), href: "/support" },
]

// Products dropdown items
const getProductItems = (t: (key: string) => string) => [
  {
    label: t("nav.products.all"),
    href: "/products",
    description: t("nav.products.allDescription")
  },
  {
    label: t("nav.products.liquidationAddress"),
    href: "/products/liquidation-address",
    description: t("nav.products.liquidationAddressDescription")
  },
  {
    label: t("nav.products.virtualAccount"),
    href: "/products/virtual-account",
    description: t("nav.products.virtualAccountDescription")
  },
  {
    label: t("nav.products.wallet"),
    href: "/products/wallet",
    description: t("nav.products.walletDescription")
  },
  {
    label: t("nav.products.payoutRoutes"),
    href: "/products/payout-routes",
    description: t("nav.products.payoutRoutesDescription")
  },
]

export function PublicHeader({
  currentSection,
  variant = "full",
  className
}: PublicHeaderProps) {
  const { t: tCommon } = useTranslation("common")
  const { t: tCta } = useTranslation("cta")
  const location = useLocation()
  const navigate = useNavigate()
  const navLinks = getNavLinks(tCommon)
  const productItems = getProductItems(tCommon)

  // Mobile menu: Products dropdown state
  const [isProductsOpen, setIsProductsOpen] = useState(false)

  // Reliable dark theme detection (same as in footers)
  const [isDark, setIsDark] = useState(false)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    // Initial check
    checkDarkMode()
    // Watch for class changes
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  // Determine active link
  const isActive = (href: string) => {
    // For hash links on landing page
    if (href.startsWith("/#")) {
      const hash = href.slice(1) // Remove leading /
      return location.pathname === "/" && location.hash === hash
    }
    // For regular page links
    return location.pathname === href || location.pathname.startsWith(href + "/")
  }

  // Handle anchor link click with smooth scroll (BUG-031)
  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Only handle anchor links
    if (!href.startsWith("/#")) return

    const hash = href.slice(2) // Remove /# to get element ID
    const element = document.getElementById(hash)

    // Calculate header height dynamically (more reliable than hardcoded value)
    const getHeaderHeight = () => {
      const header = document.querySelector('header[aria-label="Primary navigation"]')
      return header ? header.getBoundingClientRect().height : 80 // Fallback to 80px if not found
    }

    if (element) {
      e.preventDefault()
      // Update URL hash
      window.history.pushState(null, "", href)
      // Smooth scroll to element with offset for sticky header
      const headerHeight = getHeaderHeight()
      const elementPosition = element.getBoundingClientRect().top + window.scrollY
      window.scrollTo({
        top: elementPosition - headerHeight,
        behavior: "smooth"
      })
    } else if (location.pathname !== "/") {
      // If not on landing page, navigate first then scroll
      e.preventDefault()
      navigate("/")
      // Wait for navigation then scroll
      setTimeout(() => {
        const el = document.getElementById(hash)
        if (el) {
          const headerHeight = getHeaderHeight()
          const elementPosition = el.getBoundingClientRect().top + window.scrollY
          window.scrollTo({
            top: elementPosition - headerHeight,
            behavior: "smooth"
          })
          window.history.replaceState(null, "", href)
        }
      }, 100)
    }
  }, [location.pathname, navigate])

  const headerSecondaryButton = cn(
    semanticTokens.harmony.calm.bg,
    semanticTokens.harmony.calm.border,
    semanticTokens.harmony.calm.fg,
    semanticTokens.motion.fast,
    semanticTokens.radius.md,
    "px-4 py-2 hover:-translate-y-0.5"
  )

  return (
    <header
      className={cn(
        "sticky top-0 z-50",
        // Liquid Glass Chrome tier (Navigation) - FLOATING without border
        // Remove border for "floating" effect, keep only shadow
        "bg-gradient-to-br from-white/92 via-white/88 to-white/92",
        "dark:bg-gradient-to-br dark:from-[hsl(var(--card)/0.85)] dark:via-[hsl(var(--card)/0.80)] dark:to-[hsl(var(--card)/0.85)]",
        "backdrop-blur-xl backdrop-saturate-[180%]",
        "dark:backdrop-saturate-150 dark:brightness-90",
        // Shadow only (no border) for floating effect
        "shadow-[0_4px_20px_hsl(220_15%_5%/0.08)]",
        "dark:shadow-[0_4px_20px_hsl(0_0%_0%/0.3)]",
        // Safe area insets for mobile notches (BUG-009)
        "pt-[env(safe-area-inset-top)]",
        className
      )}
      aria-label="Primary navigation"
    >
      {/* Liquid Glass Specular Highlight */}
      <div className={semanticTokens.glass.specular} />
      <div className={cn(
        "mx-auto flex max-w-6xl items-center justify-between gap-3 sm:gap-4 md:gap-6",
        "px-4 sm:px-6 py-4",
        "overflow-x-hidden" // Prevent horizontal scroll (BUG-008)
      )}>
        <Link
          to="/"
          aria-label="Supabank home"
          className="flex items-center"
        >
          <SupabankLogo variant="full" size="md" inverted={isDark} />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-8 md:flex" aria-label="Main navigation">
          {navLinks.map((link) => {
            const active = currentSection
              ? link.href.includes(currentSection)
              : isActive(link.href)

            // Products dropdown
            if (link.isDropdown) {
              return (
                <DropdownMenu key={link.label}>
                  <DropdownMenuTrigger
                    asChild
                    className={cn(
                      "text-sm font-medium",
                      active ? semanticTokens.fg.default : semanticTokens.fg.muted,
                      "hover:text-primary transition-colors",
                      // Touch targets ≥44px for mobile (BUG-002)
                      "min-h-[44px] flex items-center gap-1 py-2",
                      // Focus rings (BUG-007)
                      semanticTokens.focus.ring,
                      // Touch manipulation (BUG-029)
                      "touch-manipulation"
                    )}
                  >
                    <button
                      aria-current={active ? "page" : undefined}
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      {link.label}
                      <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className={cn(
                      "w-64",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default,
                      "border"
                    )}
                  >
                    {productItems.map((item) => (
                      <DropdownMenuItem
                        key={item.href}
                        asChild
                        className={cn(
                          "flex flex-col items-start gap-1 p-3",
                          "cursor-pointer",
                          "focus:bg-accent focus:text-accent-foreground"
                        )}
                      >
                        <Link
                          to={item.href}
                          className="w-full"
                        >
                          <div className={cn(
                            "font-medium text-sm",
                            semanticTokens.fg.default
                          )}>
                            {item.label}
                          </div>
                          {item.description && (
                            <div className={cn(
                              "text-xs mt-0.5",
                              semanticTokens.fg.muted
                            )}>
                              {item.description}
                            </div>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }

            // Regular link
            return (
              <Link
                key={link.label}
                to={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "text-sm font-medium",
                  active ? semanticTokens.fg.default : semanticTokens.fg.muted,
                  "hover:text-primary transition-colors",
                  // Touch targets ≥44px for mobile (BUG-002)
                  "min-h-[44px] flex items-center py-2",
                  // Focus rings (BUG-007)
                  semanticTokens.focus.ring,
                  // Touch manipulation (BUG-029)
                  "touch-manipulation"
                )}
              >
                {link.label}
              </Link>
            )
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button variant="ghost" asChild className={headerSecondaryButton}>
            <Link to="/waitlist">{tCta("waitlist")}</Link>
          </Button>
          <Button
            size="sm"
            asChild
            className={cn(
              semanticTokens.accent.primary,
              semanticTokens.accent.hover,
              semanticTokens.fg.inverted,
              semanticTokens.shadow.sm,
              "touch-manipulation"
            )}
          >
            <Link to="/auth">{tCta("signIn")}</Link>
          </Button>
        </div>

        {/* Mobile Nav (Sheet) Liquid Glass Sheet tier */}
        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open navigation menu"
              aria-expanded="false"
              className="touch-manipulation min-h-[44px] min-w-[44px]"
            >
              <sfIcons.menu className="h-6 w-6" aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent
            aria-label="Navigation menu"
            aria-labelledby="nav-menu-title"
            className={cn(
              // Responsive width (BUG-014)
              "w-[85vw] max-w-[300px] sm:max-w-[400px]",
              // Solid white background no transparency to prevent page bleed-through
              "bg-white dark:bg-slate-900",
              "border-l border-slate-200 dark:border-slate-700",
              // Safe area insets (BUG-009)
              "pb-[env(safe-area-inset-bottom)]"
            )}
          >
            <SheetHeader className="text-left">
              <SheetTitle id="nav-menu-title" className="flex items-center">
                <SupabankLogo variant="full" size="sm" inverted={isDark} />
              </SheetTitle>
              <SheetDescription className="sr-only">
                Navigation menu with links to main sections and products
              </SheetDescription>
            </SheetHeader>
            <nav className="mt-8 flex flex-col gap-4" aria-label="Mobile navigation">
              {navLinks.map((link) => {
                const active = currentSection
                  ? link.href.includes(currentSection)
                  : isActive(link.href)

                // Products dropdown for mobile (collapsible)
                if (link.isDropdown) {
                  return (
                    <Collapsible
                      key={link.label}
                      open={isProductsOpen}
                      onOpenChange={setIsProductsOpen}
                      className="space-y-2"
                    >
                      <CollapsibleTrigger
                        className={cn(
                          "w-full text-left",
                          "text-lg font-medium",
                          active ? semanticTokens.fg.default : semanticTokens.fg.muted,
                          "min-h-[44px] flex items-center justify-between py-2",
                          "hover:text-primary transition-colors",
                          semanticTokens.focus.ring,
                          "touch-manipulation",
                          "group"
                        )}
                        aria-expanded={isProductsOpen}
                        aria-controls="products-menu"
                      >
                        <span>{link.label}</span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform duration-240 ease-standard",
                            isProductsOpen ? "rotate-180" : "rotate-0"
                          )}
                          aria-hidden="true"
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent
                        id="products-menu"
                        className="pl-4 space-y-1"
                      >
                        {productItems.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => {
                              // Close sheet after navigation (optional - can be removed if you want menu to stay open)
                              // setSheetOpen(false)
                            }}
                            className={cn(
                              "text-base font-normal block",
                              location.pathname === item.href
                                ? semanticTokens.fg.default
                                : semanticTokens.fg.muted,
                              "hover:text-primary transition-colors",
                              "min-h-[44px] flex items-center py-2",
                              semanticTokens.focus.ring,
                              "touch-manipulation"
                            )}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )
                }

                // Regular link
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={(e) => handleAnchorClick(e, link.href)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "text-lg font-medium",
                      active ? semanticTokens.fg.default : semanticTokens.fg.muted,
                      "hover:text-primary transition-colors",
                      // Touch target 44px+ (Apple HIG compliant) (BUG-002)
                      "min-h-[44px] flex items-center py-2 -my-2",
                      // Touch feedback (BUG-006)
                      "active:scale-[0.98] transition-transform duration-120",
                      "active:" + semanticTokens.bg.muted,
                      // Focus rings (BUG-007)
                      semanticTokens.focus.ring,
                      // Touch manipulation (BUG-029)
                      "touch-manipulation",
                      // Keyboard navigation (BUG-015)
                      "focus-visible:outline-none"
                    )}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <div className="mt-8 flex flex-col gap-3">
                <Button
                  asChild
                  className={cn(
                    "w-full min-h-[44px] text-base",
                    semanticTokens.accent.primary,
                    semanticTokens.accent.hover,
                    semanticTokens.fg.inverted,
                    "touch-manipulation"
                  )}
                >
                  <Link to="/auth">{tCta("signIn")}</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className={cn(
                    "w-full min-h-[44px] text-base",
                    "touch-manipulation"
                  )}
                >
                  <Link to="/waitlist">{tCta("joinWaitlist")}</Link>
                </Button>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}

// Named export only (no default export to avoid confusion)

